import React from 'react';
import { View, Text, Platform } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { SizableText } from 'tamagui';
import { XStack } from 'tamagui';
import * as Animatable from 'react-native-animatable';

interface HeaderComponentProps {
  theme: boolean;
  aiCallsData: {
    today: number;
    thisWeek: number;
    thisMonth: number;
    totalUsage: number;
  };
  contextHealthData: {
    score: number;
    status: string;
    recommendations: string[];
  };
}

// Header Component - Completely isolated to prevent re-renders
const HeaderComponent: React.FC<HeaderComponentProps> = React.memo(({ theme, aiCallsData, contextHealthData }) => {
  return (
    <Animatable.View
      animation="fadeInDown"
      duration={800}
      style={{
        backgroundColor: theme ? "#ffffff" : "#1e293b",
        borderRadius: 20,
        padding: 24,
        marginVertical: 16,
        flex: 1,
        maxWidth: 1200,
        marginHorizontal: 20,
        borderWidth: 1,
        borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 20 }}>
        <View style={{
          backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
          borderRadius: 12,
          padding: 12,
          marginRight: 16
        }}>
          <MaterialIcons
            name="psychology"
            size={28}
            color={theme ? "#0ea5e9" : "#38bdf8"}
          />
        </View>
        <View style={{ flex: 1 }}>
          <SizableText style={{
            color: theme ? "#1e293b" : "#f1f5f9",
            fontWeight: '700',
            marginBottom: 4,
            userSelect: 'none',
            pointerEvents: 'none'
          }}>
            Context Intelligence
          </SizableText>
          <SizableText numberOfLines={2} style={{
            color: theme ? "#64748b" : "#94a3b8",
            fontSize: 14,
            userSelect: 'none',
            pointerEvents: 'none'
          }}>
            AI-powered workspace analytics and health monitoring
          </SizableText>
        </View>
      </View>

      {/* Stats Grid */}
      <XStack gap={12} flexWrap="wrap" justifyContent="center" alignItems="center">
        {/* AI Calls Card */}
        <Animatable.View
          animation="bounceIn"
          delay={200}
          style={{
            flexBasis: Platform.OS === 'web' ? '48%' : '100%',
            flexGrow: 1,
            minWidth: 300,
            backgroundColor: theme ? "#dbeafe" : "#0c4a6e",
            borderRadius: 16,
            padding: 20,
            marginBottom: 8,
            borderWidth: 1,
            borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
            <MaterialIcons
              name="auto-awesome"
              size={24}
              color={theme ? "#0ea5e9" : "#38bdf8"}
              style={{ marginRight: 12 }}
            />
            <Text style={{
              fontSize: 18,
              fontWeight: '700',
              color: theme ? "#0ea5e9" : "#38bdf8"
            }}>
              AI Assistant Usage
            </Text>
          </View>

          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 16 }}>
            {[
              { label: 'Today', value: aiCallsData.today, icon: 'today' },
              { label: 'This Week', value: aiCallsData.thisWeek, icon: 'date-range' },
              { label: 'This Month', value: aiCallsData.thisMonth, icon: 'calendar-month' }
            ].map((stat, index) => (
              <View key={index} style={{ alignItems: 'center', minWidth: 80 }}>
                <MaterialIcons
                  name={stat.icon as any}
                  size={20}
                  color={theme ? "#0ea5e9" : "#38bdf8"}
                  style={{ marginBottom: 4 }}
                />
                <Text style={{
                  fontSize: 12,
                  color: theme ? "#64748b" : "#94a3b8",
                  marginBottom: 2
                }}>
                  {stat.label}
                </Text>
                <Text style={{
                  fontSize: 16,
                  fontWeight: '700',
                  color: theme ? "#0ea5e9" : "#38bdf8"
                }}>
                  {stat.value}
                </Text>
              </View>
            ))}
          </View>

          {/* Usage Progress Bar */}
          <View style={{ marginTop: 16 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
              <Text style={{
                fontSize: 12,
                color: theme ? "#64748b" : "#94a3b8"
              }}>
                Monthly Usage
              </Text>
              <Text style={{
                fontSize: 12,
                fontWeight: '600',
                color: theme ? "#0ea5e9" : "#38bdf8"
              }}>
                {aiCallsData.totalUsage}%
              </Text>
            </View>
            <View style={{
              height: 6,
              backgroundColor: theme ? "#e2e8f0" : "#374151",
              borderRadius: 3,
              overflow: 'hidden'
            }}>
              <View style={{
                height: '100%',
                width: `${aiCallsData.totalUsage}%`,
                backgroundColor: theme ? "#0ea5e9" : "#38bdf8",
                borderRadius: 3
              }} />
            </View>
          </View>
        </Animatable.View>

        {/* Context Health Card */}
        <Animatable.View
          animation="bounceIn"
          delay={400}
          style={{
            flexBasis: Platform.OS === 'web' ? '48%' : '100%',
            flexGrow: 1,
            minWidth: 300,
            backgroundColor: theme ? "#f0fdf4" : "#064e3b",
            borderRadius: 16,
            padding: 20,
            marginBottom: 8,
            borderWidth: 1,
            borderColor: theme ? "rgba(0,0,0,0.05)" : "rgba(255,255,255,0.1)"
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 16 }}>
            <MaterialIcons
              name="health-and-safety"
              size={24}
              color={theme ? "#10b981" : "#34d399"}
              style={{ marginRight: 12 }}
            />
            <Text style={{
              fontSize: 18,
              fontWeight: '700',
              color: theme ? "#10b981" : "#34d399"
            }}>
              Context Health
            </Text>
          </View>

          <View style={{ alignItems: 'center', marginBottom: 16 }}>
            <Text style={{
              fontSize: 32,
              fontWeight: '800',
              color: theme ? "#10b981" : "#34d399",
              marginBottom: 4
            }}>
              {contextHealthData.score}%
            </Text>
            <Text style={{
              fontSize: 14,
              fontWeight: '600',
              color: theme ? "#10b981" : "#34d399"
            }}>
              {contextHealthData.status}
            </Text>
          </View>

          <View style={{ gap: 8 }}>
            {contextHealthData.recommendations.slice(0, 2).map((recommendation, index) => (
              <View key={index} style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                <MaterialIcons
                  name="check-circle"
                  size={16}
                  color={theme ? "#10b981" : "#34d399"}
                  style={{ marginRight: 8, marginTop: 2 }}
                />
                <Text style={{
                  fontSize: 12,
                  color: theme ? "#64748b" : "#94a3b8",
                  flex: 1,
                  lineHeight: 16
                }}>
                  {recommendation}
                </Text>
              </View>
            ))}
          </View>
        </Animatable.View>
      </XStack>
    </Animatable.View>
  );
}, (prevProps, nextProps) => {
  // Custom comparison to prevent re-renders when only object references change
  return (
    prevProps.theme === nextProps.theme &&
    prevProps.aiCallsData.today === nextProps.aiCallsData.today &&
    prevProps.aiCallsData.thisWeek === nextProps.aiCallsData.thisWeek &&
    prevProps.aiCallsData.thisMonth === nextProps.aiCallsData.thisMonth &&
    prevProps.aiCallsData.totalUsage === nextProps.aiCallsData.totalUsage &&
    prevProps.contextHealthData.score === nextProps.contextHealthData.score &&
    prevProps.contextHealthData.status === nextProps.contextHealthData.status &&
    JSON.stringify(prevProps.contextHealthData.recommendations) === JSON.stringify(nextProps.contextHealthData.recommendations)
  );
});

export default HeaderComponent;
